
import { ISubscription } from "@/models/Subscriptions";

export interface SubscriptionValidation {
  isValid: boolean;
  isPremium: boolean;
  isExpired: boolean;
  status: string | null;
  daysRemaining: number | null;
  reason: 'no_subscription' | 'expired' | 'past_due' | 'canceled' | 'active' | 'trial_expired';
}

/**
 * Validates a subscription object to check if it grants access to premium features.
 * Checks for:
 * 1. Existence of subscription
 * 2. Premium plan type
 * 3. Status (active or trial)
 * 4. Expiration date (currentPeriodEnd)
 */
export function validateSubscription(subscription: ISubscription | null | undefined): SubscriptionValidation {
  if (!subscription) {
    return {
      isValid: false,
      isPremium: false,
      isExpired: false,
      status: null,
      daysRemaining: null,
      reason: 'no_subscription',
    };
  }

  const now = new Date();
  const currentPeriodEnd = new Date(subscription.currentPeriodEnd);
  // Calculate days remaining (can be negative if expired)
  const diffTime = currentPeriodEnd.getTime() - now.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const isPremium = subscription.planType?.toUpperCase().includes("PREMIUM") || false;
  
  // Check if actually expired based on date
  // We allow a small buffer of 1 day to account for timezone differences or billing processing delays
  const isExpiredByDate = daysRemaining < -1; 

  // Check specific statuses that denote invalid access
  const isInvalidStatus = 
    subscription.status === 'canceled' || 
    subscription.status === 'past_due' || 
    subscription.status === 'incomplete_expired' ||
    subscription.status === 'incomplete';

  if (isInvalidStatus) {
    return {
      isValid: false,
      isPremium,
      isExpired: true,
      status: subscription.status,
      daysRemaining,
      reason: subscription.status as any,
    };
  }

  if (isExpiredByDate) {
    return {
      isValid: false,
      isPremium,
      isExpired: true,
      status: subscription.status,
      daysRemaining,
      reason: 'expired',
    };
  }

  return {
    isValid: true,
    isPremium,
    isExpired: false,
    status: subscription.status,
    daysRemaining,
    reason: 'active',
  };
}

export function isSubscriptionActive(subscription: ISubscription | null | undefined): boolean {
  const validation = validateSubscription(subscription);
  return validation.isValid;
}

export function getPremiumAccess(subscription: ISubscription | null | undefined): boolean {
  const validation = validateSubscription(subscription);
  return validation.isValid && validation.isPremium;
}

/**
 * Client-side check for active premium access using plan type and status from the Zustand store.
 * Returns true only if the plan includes "PREMIUM" AND the subscription status is "active".
 */
export function hasActivePremium(planType: string | null | undefined, status: string | null | undefined): boolean {
  if (!planType || !status) return false;
  const isPremiumPlan = planType.toUpperCase().includes("PREMIUM");
  const isActive = status === "active";
  return isPremiumPlan && isActive;
}

/**
 * Client-side check: returns the effective plan type for feature limit purposes.
 * If the subscription is not active, returns "BASIC" to enforce base-level limits.
 */
export function getEffectivePlanType(planType: string | null | undefined, status: string | null | undefined): string | null {
  if (!planType) return null;
  if (!status || status !== "active") return "BASIC";
  return planType;
}
